import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { operatorService } from "../../../services";
import { ChatMessageInfo } from "../../../types/operator";
import LoadingSpinner from "../../common/LoadingSpinner";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";

// 채팅방별 그룹화된 데이터 타입
interface ChatGroup {
  chatId: string;
  chatTitle: string;
  messages: ChatMessageInfo[];
  lastMessageTime: string;
}

const OperatorUserChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const [messages, setMessages] = useState<ChatMessageInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedChatId, setExpandedChatId] = useState<string | null>(null);
  const limit = 20;

  // 채팅 이력 조회
  const fetchUserChats = useCallback(
    async (page: number) => {
      if (!userId) return;

      setIsLoading(true);
      try {
        const response = await operatorService.getUserChats(userId, {
          page,
          limit,
        });

        if (response.success && response.data) {
          setMessages(response.data.messages);
          setCurrentPage(response.data.page);
          setTotalPages(response.data.totalPages);
        } else {
          toast.error("채팅 이력을 불러오는데 실패했습니다.");
        }
      } catch (error) {
        console.error("Error fetching user chats:", error);
        toast.error("채팅 이력을 불러오는데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    },
    [userId, limit]
  );

  // 초기 데이터 로드
  useEffect(() => {
    fetchUserChats(1);
  }, [fetchUserChats]);

  // 메시지를 채팅방별로 그룹화
  const chatGroups = useMemo(() => {
    const groups: Record<string, ChatGroup> = {};

    messages.forEach((message) => {
      if (!groups[message.chatId]) {
        groups[message.chatId] = {
          chatId: message.chatId,
          chatTitle: message.chatTitle,
          messages: [],
          lastMessageTime: message.questionCreatedAt,
        };
      }
      groups[message.chatId].messages.push(message);
      // 가장 최근 메시지 시간 업데이트
      if (
        new Date(message.questionCreatedAt) >
        new Date(groups[message.chatId].lastMessageTime)
      ) {
        groups[message.chatId].lastMessageTime = message.questionCreatedAt;
      }
    });

    // 각 채팅방의 메시지를 최신순(최근 순)으로 정렬
    Object.values(groups).forEach((group) => {
      group.messages.sort(
        (a, b) =>
          new Date(b.questionCreatedAt).getTime() -
          new Date(a.questionCreatedAt).getTime()
      );
    });

    // 배열로 변환하고 채팅방은 오래된 순 정렬
    return Object.values(groups).sort(
      (a, b) =>
        new Date(b.lastMessageTime).getTime() -
        new Date(a.lastMessageTime).getTime()
    );
  }, [messages]);

  // 페이지 변경
  const handlePageChange = (page: number) => {
    fetchUserChats(page);
  };

  // 날짜 포맷
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 날짜 포맷 (간단)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // 검수 상태 뱃지
  const getReviewStatusBadge = (status: string) => {
    const statusMap: Record<string, { text: string; className: string }> = {
      PENDING: { text: "대기", className: "bg-gray-100 text-gray-800" },
      APPROVED: { text: "승인", className: "bg-green-100 text-green-800" },
      REJECTED: { text: "거부", className: "bg-red-100 text-red-800" },
    };

    const statusInfo = statusMap[status] || {
      text: status,
      className: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusInfo.className}`}
      >
        {statusInfo.text}
      </span>
    );
  };

  // 채팅방 토글
  const toggleChat = (chatId: string) => {
    setExpandedChatId(expandedChatId === chatId ? null : chatId);
  };

  return (
    <div className="h-screen-dvh bg-gray-50 flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/operator/users")}
            className="mr-3 text-gray-600 hover:text-gray-800"
          >
            <Icon name="chevronLeft" size="md" />
          </button>
          <h1 className="text-lg font-medium text-gray-800">채팅 이력</h1>
        </div>
        <div className="text-sm text-gray-600">
          전체 {chatGroups.length}개 대화
        </div>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : chatGroups.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Icon
                name="messageSquare"
                className="text-gray-400 mx-auto mb-4"
                size="xl"
              />
              <p className="text-gray-600">채팅 이력이 없습니다.</p>
            </div>
          ) : (
            <>
              {/* 채팅방 목록 */}
              <div className="space-y-4">
                {chatGroups.map((chatGroup) => (
                  <div
                    key={chatGroup.chatId}
                    className="bg-white rounded-lg shadow-sm overflow-hidden"
                  >
                    {/* 헤더 */}
                    <div
                      className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleChat(chatGroup.chatId)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon
                              name="messageSquare"
                              className="text-purple-600"
                              size="sm"
                            />
                            <h3 className="text-base font-semibold text-gray-800">
                              {chatGroup.chatTitle}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-gray-500">
                              {formatDate(chatGroup.lastMessageTime)}
                            </p>
                            <span className="text-xs text-gray-400">•</span>
                            <p className="text-xs text-gray-500">
                              {chatGroup.messages.length}개의 대화
                            </p>
                          </div>
                        </div>
                        <button className="ml-2 text-gray-400 hover:text-gray-600">
                          <Icon
                            name={
                              expandedChatId === chatGroup.chatId
                                ? "chevronUp"
                                : "chevronDown"
                            }
                            size="sm"
                          />
                        </button>
                      </div>
                    </div>

                    {/* 대화 내용 */}
                    {expandedChatId === chatGroup.chatId && (
                      <div className="p-4 bg-gray-50">
                        <div className="space-y-4">
                          {chatGroup.messages.map((message) => (
                            <div key={message.id} className="space-y-3">
                              {/* 답변 */}
                              <div className="flex gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                    <Icon
                                      name="bot"
                                      className="text-purple-600"
                                      size="xs"
                                    />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="bg-purple-50 rounded-lg p-3 shadow-sm">
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                      {message.aiResponse}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                      <p className="text-xs text-gray-500">
                                        {formatDateTime(
                                          message.responseCreatedAt
                                        )}
                                      </p>
                                      {getReviewStatusBadge(
                                        message.reviewStatus
                                      )}
                                    </div>
                                  </div>

                                  {/* 검수 정보 */}
                                  {message.reviewedAt && (
                                    <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                                      <div className="flex items-center gap-1 mb-1">
                                        <Icon
                                          name="checkCircle"
                                          className="text-gray-400"
                                          size="xs"
                                        />
                                        <span className="text-xs font-medium text-gray-700">
                                          검수 정보
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-600">
                                        검수일:{" "}
                                        {formatDateTime(message.reviewedAt)}
                                      </p>
                                      {message.reviewReason && (
                                        <p className="text-xs text-gray-600 mt-1">
                                          사유: {message.reviewReason}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* 질문 */}
                              <div className="flex gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Icon
                                      name="user"
                                      className="text-blue-600"
                                      size="xs"
                                    />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="bg-white rounded-lg p-3 shadow-sm">
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                      {message.userQuestion}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-2">
                                      {formatDateTime(
                                        message.questionCreatedAt
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    이전
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum ? "primary" : "outline"
                          }
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    다음
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default OperatorUserChatsPage;
